import { selectLatestComponent } from "@/lib/component-selection";
import { StateCreator } from "zustand";
import GeneralService from "@/core/services/GeneralService";
import { Term, Area, Component } from "@/core/models/General.model";
import { DashboardComponent } from "@/core/models/DashboardComponent.model";

export interface GeneralState {
  PhoneCountries: any | null;
  Terms: Term[] | null;
  aboutUs: Term[] | null;
  Governorates: Area[] | null;
  Areas: Area[] | null;
  isLoadingGovernorates: boolean;
  isLoadingAreas: boolean;
  isLoadingDashboard: boolean;
  Component: Component | null;
  isLoadingComponent: boolean;
  // Separate states for each component type
  MetaComponent: Component | null;
  HomeComponent: Component | null;
  isLoadingHomeComponent: boolean;
  PlanComponent: Component | null;
  isLoadingPlanComponent: boolean;
  AboutComponent: Component | null;
  isLoadingAboutComponent: boolean;
  DonateComponent: Component | null;
  isLoadingDonateComponent: boolean;
  ContactComponent: Component | null;
  isLoadingContactComponent: boolean;
  VolunteerComponent: Component | null;
  isLoadingVolunteerComponent: boolean;
  SocialLinksComponent: Component[] | null;
  isLoadingSocialLinksComponent: boolean;
  EventsComponent: Component | null;
  isLoadingEventsComponent: boolean;
  ServicesComponent: Component | null;
  isLoadingServicesComponent: boolean;
  GovernoratesComponent: DashboardComponent | null;
  isLoadingGovernoratesComponent: boolean;
  BlogsComponent: any[] | null;
  isLoadingBlogsComponent: boolean;
  BlogDetailComponent: any | null;
  isLoadingBlogDetailComponent: boolean;
  isLoadingAllComponents: boolean;
  fetchPhoneCountries: () => Promise<void>;
  fetchTerms: () => Promise<void>;
  fetchGetAboutUs: () => Promise<void>;
  fetchAreas: (countryId?: number) => Promise<void>;
  fetchComponent: (componentType: string) => Promise<void>;
  fetchHomeComponent: () => Promise<void>;
  fetchPlanComponent: () => Promise<void>;
  fetchAboutComponent: (lang?: string) => Promise<void>;
  fetchDonateComponent: () => Promise<void>;
  fetchContactComponent: () => Promise<void>;
  fetchVolunteerComponent: () => Promise<void>;
  fetchSocialLinksComponent: () => Promise<void>;
  fetchEventsComponent: () => Promise<void>;
  fetchServicesComponent: () => Promise<void>;
  fetchGovernoratesComponent: () => Promise<void>;
  fetchBlogsComponent: () => Promise<void>;
  fetchBlogDetailComponent: (id: number) => Promise<void>;
  fetchAllComponents: () => Promise<void>;
  initializeComponents: (components: {
    HomeComponent?: Component | null;
    PlanComponent?: Component | null;
    AboutComponent?: Component | null;
    DonateComponent?: Component | null;
    ContactComponent?: Component | null;
    VolunteerComponent?: Component | null;
    SocialLinksComponent?: Component[] | null;
    EventsComponent?: Component | null;
    ServicesComponent?: Component | null;
    GovernoratesComponent?: Component | null;
  }) => void;
  resetGovernorates: () => void;
  resetAreas: () => void;
  resetComponent: () => void;
  resetHomeComponent: () => void;
  resetPlanComponent: () => void;
  resetAboutComponent: () => void;
  resetDonateComponent: () => void;
  resetContactComponent: () => void;
  resetVolunteerComponent: () => void;
  resetSocialLinksComponent: () => void;
  resetEventsComponent: () => void;
  resetServicesComponent: () => void;
  resetGovernoratesComponent: () => void;
  resetBlogsComponent: () => void;
  resetBlogDetailComponent: () => void;
}

const createGeneralSlice: StateCreator<GeneralState> = (set) => ({
  PhoneCountries: null,
  Terms: null,
  aboutUs: null,
  Governorates: null,
  Areas: null,
  isLoadingGovernorates: false,
  isLoadingAreas: false,
  Dashboard: null,
  isLoadingDashboard: false,
  Component: null,
  isLoadingComponent: false,
  MetaComponent: null,
  HomeComponent: null,
  isLoadingHomeComponent: false,
  PlanComponent: null,
  isLoadingPlanComponent: false,
  AboutComponent: null,
  isLoadingAboutComponent: false,
  DonateComponent: null,
  isLoadingDonateComponent: false,
  ContactComponent: null,
  isLoadingContactComponent: false,
  VolunteerComponent: null,
  isLoadingVolunteerComponent: false,
  SocialLinksComponent: null,
  isLoadingSocialLinksComponent: false,
  EventsComponent: null,
  isLoadingEventsComponent: false,
  ServicesComponent: null,
  isLoadingServicesComponent: false,
  GovernoratesComponent: null,
  isLoadingGovernoratesComponent: false,
  BlogsComponent: null,
  isLoadingBlogsComponent: false,
  BlogDetailComponent: null,
  isLoadingBlogDetailComponent: false,
  isLoadingAllComponents: false,

  fetchPhoneCountries: async () => {
    const response = await GeneralService.GetPhoneCountries();
    set({ PhoneCountries: response?.data ?? null });
  },

  fetchTerms: async () => {
    const response = await GeneralService.GetTerms();
    set({ Terms: response?.data ?? null });
  },

  fetchGetAboutUs: async () => {
    const response = await GeneralService.GetAboutUs();
    set({ aboutUs: response?.data ?? null });
  },

  fetchAreas: async (countryId) => {
    set({ isLoadingGovernorates: true, isLoadingAreas: true });
    try {
      const response = await GeneralService.GetAllAreas(countryId);
      const data = response?.data ?? [];
      set({
        Governorates: data.filter((item) => item.parentId === null),
        Areas: data.filter((item) => item.parentId !== null),
      });
    } finally {
      set({ isLoadingGovernorates: false, isLoadingAreas: false });
    }
  },

  fetchComponent: async (componentType: string) => {
    set({ isLoadingComponent: true });
    try {
      const response = await GeneralService.GetComponent(componentType);
      set({ Component: response.data ?? null });
    } catch (error) {
      console.error("Error fetching component:", error);
    } finally {
      set({ isLoadingComponent: false });
    }
  },

  resetGovernorates: () => {
    set({ Governorates: null });
  },

  resetAreas: () => {
    set({ Areas: null });
  },

  resetComponent: () => {
    set({ Component: null, isLoadingComponent: false });
  },

  fetchHomeComponent: async () => {
    set({ isLoadingHomeComponent: true });
    try {
      const response = await GeneralService.GetComponent("Home");
      const data = selectLatestComponent(response.data);
      set({ HomeComponent: data ?? null });
    } catch (error) {
      console.error("Error fetching home component:", error);
    } finally {
      set({ isLoadingHomeComponent: false });
    }
  },

  fetchPlanComponent: async () => {
    set({ isLoadingPlanComponent: true });
    try {
      const response = await GeneralService.GetComponent("PlanYourVisit");
      const data = selectLatestComponent(response.data);
      set({ PlanComponent: data ?? null });
    } catch (error) {
      console.error("Error fetching plan component:", error);
    } finally {
      set({ isLoadingPlanComponent: false });
    }
  },

  fetchAboutComponent: async (lang?: string) => {
    set({ isLoadingAboutComponent: true, AboutComponent: null });
    try {
      const response = await GeneralService.GetComponent("About");
      const dataArray = Array.isArray(response.data)
        ? response.data
        : response.data
          ? [response.data]
          : [];

      let data: Component | null = null;

      // Filter by language if provided, otherwise get the most recent one
      if (lang && dataArray.length > 0) {
        const languageComponents = dataArray.filter(
          (component: Component) => component?.lang === lang,
        );
        if (languageComponents.length > 0) {
          // Get the most recent one (highest ID)
          data = languageComponents.reduce(
            (prev: Component, current: Component) =>
              current.id > prev.id ? current : prev,
          );
        }
      } else if (dataArray.length > 0) {
        // Get the most recent one (highest ID) if no language specified
        data = dataArray.reduce((prev: Component, current: Component) =>
          current.id > prev.id ? current : prev,
        );
      }

      set({ AboutComponent: data });
    } catch (error) {
      console.error("Error fetching about component:", error);
    } finally {
      set({ isLoadingAboutComponent: false });
    }
  },

  resetHomeComponent: () => {
    set({ HomeComponent: null, isLoadingHomeComponent: false });
  },

  resetPlanComponent: () => {
    set({ PlanComponent: null, isLoadingPlanComponent: false });
  },

  resetAboutComponent: () => {
    set({ AboutComponent: null, isLoadingAboutComponent: false });
  },

  fetchDonateComponent: async () => {
    set({ isLoadingDonateComponent: true });
    try {
      const response = await GeneralService.GetComponent("Donate");
      const data = selectLatestComponent(response.data);
      set({ DonateComponent: data ?? null });
    } catch (error) {
      console.error("Error fetching donate component:", error);
    } finally {
      set({ isLoadingDonateComponent: false });
    }
  },

  resetDonateComponent: () => {
    set({ DonateComponent: null, isLoadingDonateComponent: false });
  },

  fetchContactComponent: async () => {
    set({ isLoadingContactComponent: true });
    try {
      const response = await GeneralService.GetComponent("Contact");
      const data = selectLatestComponent(response.data);
      set({ ContactComponent: data ?? null });
    } catch (error) {
      console.error("Error fetching contact component:", error);
    } finally {
      set({ isLoadingContactComponent: false });
    }
  },

  resetContactComponent: () => {
    set({ ContactComponent: null, isLoadingContactComponent: false });
  },

  fetchVolunteerComponent: async () => {
    set({ isLoadingVolunteerComponent: true });
    try {
      const response = await GeneralService.GetComponent("Volunteer");
      const data = selectLatestComponent(response.data);
      set({ VolunteerComponent: data ?? null });
    } catch (error) {
      console.error("Error fetching volunteer component:", error);
    } finally {
      set({ isLoadingVolunteerComponent: false });
    }
  },

  resetVolunteerComponent: () => {
    set({ VolunteerComponent: null, isLoadingVolunteerComponent: false });
  },

  fetchSocialLinksComponent: async () => {
    set({ isLoadingSocialLinksComponent: true });
    try {
      const response = await GeneralService.GetComponent("SocialLinks");
      const data = Array.isArray(response.data)
        ? response.data
        : response.data
          ? [response.data]
          : [];
      set({ SocialLinksComponent: data.length > 0 ? data : null });
    } catch (error) {
      console.error("Error fetching social links component:", error);
    } finally {
      set({ isLoadingSocialLinksComponent: false });
    }
  },

  resetSocialLinksComponent: () => {
    set({ SocialLinksComponent: null, isLoadingSocialLinksComponent: false });
  },

  fetchEventsComponent: async () => {
    set({ isLoadingEventsComponent: true });
    try {
      const response = await GeneralService.GetComponent("Events");
      const data = selectLatestComponent(response.data);
      set({ EventsComponent: data ?? null });
    } catch (error) {
      console.error("Error fetching events component:", error);
    } finally {
      set({ isLoadingEventsComponent: false });
    }
  },

  resetEventsComponent: () => {
    set({ EventsComponent: null, isLoadingEventsComponent: false });
  },

  fetchServicesComponent: async () => {
    set({ isLoadingServicesComponent: true });
    try {
      const response = await GeneralService.GetComponent("Services");
      const data = selectLatestComponent(response.data);
      set({ ServicesComponent: data ?? null });
    } catch (error) {
      console.error("Error fetching services component:", error);
    } finally {
      set({ isLoadingServicesComponent: false });
    }
  },

  resetServicesComponent: () => {
    set({ ServicesComponent: null, isLoadingServicesComponent: false });
  },

  fetchGovernoratesComponent: async () => {
    set({ isLoadingGovernoratesComponent: true });
    try {
      const response = await GeneralService.GetComponent("Governorates");
      const data = selectLatestComponent(response.data);
      set({ GovernoratesComponent: data ?? null });
    } catch (error) {
      console.error("Error fetching governorates component:", error);
    } finally {
      set({ isLoadingGovernoratesComponent: false });
    }
  },

  resetGovernoratesComponent: () => {
    set({ GovernoratesComponent: null, isLoadingGovernoratesComponent: false });
  },

  fetchBlogsComponent: async () => {
    set({ isLoadingBlogsComponent: true });
    try {
      const response = await GeneralService.GetComponent("blogs");
      const data = Array.isArray(response.data) ? response.data : [];
      set({ BlogsComponent: data.length > 0 ? data : null });
    } catch (error) {
      console.error("Error fetching blogs component:", error);
    } finally {
      set({ isLoadingBlogsComponent: false });
    }
  },

  resetBlogsComponent: () => {
    set({ BlogsComponent: null, isLoadingBlogsComponent: false });
  },

  fetchBlogDetailComponent: async (id: number) => {
    set({ isLoadingBlogDetailComponent: true });
    try {
      const response = await GeneralService.GetComponentById("blogs", id);
      set({ BlogDetailComponent: response.data ?? null });
    } catch (error) {
      console.error("Error fetching blog detail component:", error);
    } finally {
      set({ isLoadingBlogDetailComponent: false });
    }
  },

  resetBlogDetailComponent: () => {
    set({ BlogDetailComponent: null, isLoadingBlogDetailComponent: false });
  },

  initializeComponents: (components) => {
    set({
      HomeComponent: components.HomeComponent ?? null,
      PlanComponent: components.PlanComponent ?? null,
      AboutComponent: components.AboutComponent ?? null,
      DonateComponent: components.DonateComponent ?? null,
      ContactComponent: components.ContactComponent ?? null,
      VolunteerComponent: components.VolunteerComponent ?? null,
      SocialLinksComponent: components.SocialLinksComponent ?? null,
      EventsComponent: components.EventsComponent ?? null,
      ServicesComponent: components.ServicesComponent ?? null,
      GovernoratesComponent: components.GovernoratesComponent ?? null,
      isLoadingAllComponents: false,
    });
  },

  fetchAllComponents: async () => {
    set({ isLoadingAllComponents: true });
    try {
      const [
        homeResponse,
        planResponse,
        aboutResponse,
        donateResponse,
        contactResponse,
        volunteerResponse,
        socialLinksResponse,
        eventsResponse,
        servicesResponse,
      ] = await Promise.all(
        [
          GeneralService.GetComponent("Home"),
          GeneralService.GetComponent("PlanYourVisit"),
          GeneralService.GetComponent("About"),
          GeneralService.GetComponent("Donate"),
          GeneralService.GetComponent("Contact"),
          GeneralService.GetComponent("Volunteer"),
          GeneralService.GetComponent("SocialLinks"),
          GeneralService.GetComponent("Events"),
          GeneralService.GetComponent("Services"),
        ].map((request) => request.catch(() => ({ data: null }))),
      );

      const homeData = selectLatestComponent(homeResponse.data);
      const planData = selectLatestComponent(planResponse.data);
      const aboutData = selectLatestComponent(aboutResponse.data);
      const donateData = selectLatestComponent(donateResponse.data);
      const contactData = selectLatestComponent(contactResponse.data);
      const volunteerData = selectLatestComponent(volunteerResponse.data);
      const socialLinksData = Array.isArray(socialLinksResponse.data)
        ? socialLinksResponse.data
        : socialLinksResponse.data
          ? [socialLinksResponse.data]
          : [];
      const eventsData = selectLatestComponent(eventsResponse.data);
      const servicesData = selectLatestComponent(servicesResponse.data);

      set({
        HomeComponent: homeData ?? null,
        PlanComponent: planData ?? null,
        AboutComponent: aboutData ?? null,
        DonateComponent: donateData ?? null,
        ContactComponent: contactData ?? null,
        VolunteerComponent: volunteerData ?? null,
        SocialLinksComponent:
          socialLinksData.length > 0 ? socialLinksData : null,
        EventsComponent: eventsData ?? null,
        ServicesComponent: servicesData ?? null,
      });
    } catch (error) {
      console.error("Error fetching all components:", error);
    } finally {
      set({ isLoadingAllComponents: false });
    }
  },
});

export default createGeneralSlice;
